import { Component, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ThemeService } from '../../services/theme.service';
import { AuthService } from '../../services/auth.service';
import { AuthModalComponent } from '../auth-modal/auth-modal.component';
import { SettingsModalComponent } from '../settings-modal/settings-modal.component';
import { slideInUp } from '../../animations/animations';
import { map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule, AuthModalComponent, SettingsModalComponent],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  animations: [slideInUp]
})
export class NavbarComponent {
  private themeService = inject(ThemeService);
  private router = inject(Router);
  authService = inject(AuthService);
  
  isDarkMode$ = this.themeService.darkMode$;
  isMenuOpen = false;
  showAuthModal = false;
  showSettingsModal = false;
  showUserDropdown = false;

  // Observable that emits true only if user is logged in AND is NOT an admin
  isRegularUser$ = this.authService.user$.pipe(
    switchMap(user => {
      if (!user) return of(false);
      // Signed custom claim from the ID token (no Firestore read needed).
      return this.authService.getAdminStatus(user, false).then(status => !status.isAdmin);
    })
  );

  toggleTheme() {
    this.themeService.toggleTheme();
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu() {
    this.isMenuOpen = false;
  }

  openAuthModal() {
    this.showAuthModal = true;
    this.isMenuOpen = false;
  }

  closeAuthModal() {
    this.showAuthModal = false;
  }

  openSettings() {
    this.showSettingsModal = true;
    this.showUserDropdown = false;
    this.isMenuOpen = false;
  }

  closeSettings() {
    this.showSettingsModal = false;
  }

  toggleUserDropdown() {
    this.showUserDropdown = !this.showUserDropdown;
  }

  closeUserDropdown() {
    this.showUserDropdown = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-menu')) {
      this.showUserDropdown = false;
    }
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (this.isMenuOpen) {
      this.isMenuOpen = false;
    }
  }

  async logout() {
    this.showUserDropdown = false;
    await this.authService.signOut();
  }

  async navigateToSubscribe() {
    this.isMenuOpen = false;
    await this.router.navigate(['/'], { fragment: 'subscription-section' });
    setTimeout(() => {
      const element = document.getElementById('subscription-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }
}
